import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Material, ShippingOption, CoatingType } from '../types';
import { obfuscateData } from './security';
import { STANDARD_SHEET_SIZES } from './sheetSizes';
import { fetchPricingConfig, updatePricingConfig } from './api';
import { fetchWithInterceptors } from './apiInterceptors';

/**
 * Transform backend data structure to frontend format
 */
function transformBackendData(backendData: Record<string, unknown>): PricingData {
  const transformed = { ...backendData };
  
  // Transform materialPerKgRates from backend nested format to frontend flat format
  if (backendData.materialPerKgRates) {
    const transformedMaterials: Record<string, Record<string, number>> = {};
    
    Object.entries(backendData.materialPerKgRates).forEach(([material, data]: [string, Record<string, unknown>]) => {
      if (material === 'corrugated') {
        // Handle corrugated variants
        transformedMaterials[material] = {};
        if (data.variants) {
          Object.entries(data.variants).forEach(([variant, variantData]: [string, {pricePerKg?: number}]) => {
            transformedMaterials[material][variant] = variantData.pricePerKg || 0;
          });
        }
      } else {
        // Handle GSM-based materials
        transformedMaterials[material] = {};
        if (data.gsmOptions) {
          Object.entries(data.gsmOptions).forEach(([gsm, gsmData]: [string, {pricePerKg?: number}]) => {
            transformedMaterials[material][gsm] = gsmData.pricePerKg || 0;
          });
        }
      }
    });
    
    transformed.materialPerKgRates = transformedMaterials;
  }
  
  // Pass-through Spot UV sheet pricing if present
  if (backendData.spotUvSheetPricing) {
    transformed.spotUvSheetPricing = backendData.spotUvSheetPricing;
  }
  
  // Pass-through Spot UV wastage settings if present
  if (backendData.spotUvWastage) {
    transformed.spotUvWastage = backendData.spotUvWastage;
  }
  
  // Pass-through foiling configuration if present or set defaults
  if (backendData.foiling) {
    transformed.foiling = backendData.foiling;
  } else {
    // Set default foiling configuration if not present
    transformed.foiling = {
      blockRatePerSqIn: 120,
      blockMinimumCost: 3500,
      sheetCategories: {
        small: {
          description: "Sheet size ≤18×25 (≤450 sq in)",
          quantityTiers: [
            {
              minQty: 1,
              maxQty: 300,
              perSheetRate: 20,
              minimumCost: 6000
            },
            {
              minQty: 301,
              maxQty: 1500,
              perSheetRate: 8.5,
              blockSqInRate: 0.07,
              minimumCost: 8000
            },
            {
              minQty: 1501,
              maxQty: null,
              perSheetRate: 5,
              blockSqInRate: 0.07,
              minimumCost: 10000
            }
          ]
        },
        medium: {
          description: "Sheet size 18×25 to 25×36 (450-900 sq in)",
          quantityTiers: [
            {
              minQty: 1,
              maxQty: 300,
              perSheetRate: 28,
              minimumCost: 8000
            },
            {
              minQty: 301,
              maxQty: 1500,
              perSheetRate: 10,
              blockSqInRate: 0.07,
              minimumCost: 12000
            },
            {
              minQty: 1501,
              maxQty: null,
              perSheetRate: 7,
              blockSqInRate: 0.07,
              minimumCost: 14000
            }
          ]
        },
        large: {
          description: "Sheet size 25×36 to 28×40 (900-1120 sq in)",
          quantityTiers: [
            {
              minQty: 1,
              maxQty: 300,
              perSheetRate: 35,
              minimumCost: 10000
            },
            {
              minQty: 301,
              maxQty: 1500,
              perSheetRate: 13.5,
              blockSqInRate: 0.07,
              minimumCost: 16000
            },
            {
              minQty: 1501,
              maxQty: null,
              perSheetRate: 10,
              blockSqInRate: 0.07,
              minimumCost: 22000
            }
          ]
        }
      }
    };
  }

  // Pass through printing configuration (plates and color rates)
  if (backendData.printing) {
    transformed.printing = backendData.printing as PricingData['printing'];
  }

  // Pass through shipping configuration if provided
  if (backendData.shipping) {
    transformed.shipping = backendData.shipping as PricingData['shipping'];
  }
  
  // Extract area-based rates from finishingProcesses (except pvc-window which uses sheet-based pricing)
  if (backendData.finishingProcesses) {
    const areaBasedRates: Record<string, Record<string, number>> = {};

    // Extract area-based rates from each finishing process
    ['embossing', 'debossing', 'foiling', 'spot-uv'].forEach(process => {
      const processData = backendData.finishingProcesses[process];
      if (processData?.areaBasedRate !== undefined) {
        // Map server keys to frontend keys
        if (process === 'spot-uv') {
          areaBasedRates.spotUv = processData.areaBasedRate;
        } else {
          areaBasedRates[process] = processData.areaBasedRate;
        }
      }
    });

    if (Object.keys(areaBasedRates).length > 0) {
      transformed.areaBasedRates = areaBasedRates;
    }

    // Extract PVC Window sheet-based configuration
    const pvcData = backendData.finishingProcesses['pvc-window'];
    if (pvcData) {
      transformed.pvcWindow = {
        sheetSize: pvcData.sheetSize || { width: 24, height: 54 },
        costPerSheet: pvcData.costPerSheet || 500,
        minimumCost: pvcData.minimumCost || 2500
      };
    }
  }
  
  return transformed;
}

/**
 * Transform frontend data structure to backend format
 */
function transformFrontendToBackend(frontendData: Record<string, unknown>): Record<string, unknown> {
  const transformed = { ...frontendData };
  
  // Transform materialPerKgRates from frontend flat format to backend nested format
  if (frontendData.materialPerKgRates) {
    const transformedMaterials: Record<string, {variants?: Record<string, {pricePerKg: number}>; gsmOptions?: Record<string, {pricePerKg: number}>}> = {};
    
    Object.entries(frontendData.materialPerKgRates).forEach(([material, rates]: [string, Record<string, number>]) => {
      if (material === 'corrugated') {
        // Handle corrugated variants
        transformedMaterials[material] = {
          variants: {}
        };
        Object.entries(rates).forEach(([variant, rate]: [string, number]) => {
          transformedMaterials[material].variants[variant] = {
            pricePerKg: rate
          };
        });
      } else {
        // Handle GSM-based materials
        transformedMaterials[material] = {
          gsmOptions: {}
        };
        Object.entries(rates).forEach(([gsm, rate]: [string, number]) => {
          transformedMaterials[material].gsmOptions[gsm] = {
            pricePerKg: rate
          };
        });
      }
    });
    
    transformed.materialPerKgRates = transformedMaterials;
  }
  
  // Pass-through Spot UV sheet pricing updates
  if (frontendData.spotUvSheetPricing) {
    transformed.spotUvSheetPricing = frontendData.spotUvSheetPricing;
  }
  
  // Pass-through Spot UV wastage settings updates
  if (frontendData.spotUvWastage) {
    transformed.spotUvWastage = frontendData.spotUvWastage;
  }
  
  // Pass-through foiling configuration updates
  if (frontendData.foiling) {
    transformed.foiling = frontendData.foiling;
  }
  
  // Transform area-based rates to finishingProcesses format
  if (frontendData.areaBasedRates) {
    if (!transformed.finishingProcesses) {
      transformed.finishingProcesses = {};
    }
    
    const areaBasedRates = frontendData.areaBasedRates;
    
    // Map frontend keys to server keys and update area-based rates
    if (areaBasedRates.embossing !== undefined) {
      if (!transformed.finishingProcesses.embossing) transformed.finishingProcesses.embossing = {};
      transformed.finishingProcesses.embossing.areaBasedRate = areaBasedRates.embossing;
    }
    if (areaBasedRates.debossing !== undefined) {
      if (!transformed.finishingProcesses.debossing) transformed.finishingProcesses.debossing = {};
      transformed.finishingProcesses.debossing.areaBasedRate = areaBasedRates.debossing;
    }
    if (areaBasedRates.foiling !== undefined) {
      if (!transformed.finishingProcesses.foiling) transformed.finishingProcesses.foiling = {};
      transformed.finishingProcesses.foiling.areaBasedRate = areaBasedRates.foiling;
    }
    if (areaBasedRates.spotUv !== undefined) {
      if (!transformed.finishingProcesses['spot-uv']) transformed.finishingProcesses['spot-uv'] = {};
      transformed.finishingProcesses['spot-uv'].areaBasedRate = areaBasedRates.spotUv;
    }
    // Note: pvcWindow no longer uses areaBasedRates - it uses sheet-based pricing
  }

  // Transform PVC Window sheet-based configuration
  if (frontendData.pvcWindow) {
    if (!transformed.finishingProcesses) transformed.finishingProcesses = {};
    transformed.finishingProcesses['pvc-window'] = frontendData.pvcWindow;
  }

  return transformed;
}

// Define sheet size interface
export interface SheetSize {
  width: number;
  height: number;
}

// Define the structure for pricing data
export interface PricingData {
  materialPerKgRates: Record<Material, Record<string, number>>;
  featureCosts: Record<string, number>;
  shippingRates: Record<ShippingOption, { baseRate: number; perKgRate: number }>;
  shipping?: Record<string, unknown>;
  laminationRates: Record<CoatingType, number>;
  sheetSizes: SheetSize[];
  dieCuttingRates: Record<string, number>; // Rates for die cutting based on sheet sizes
  pastingRates: Record<string, number>; // Rates for pasting based on sheet sizes
  embossingRates: Record<string, number>; // Rates for embossing based on sheet sizes (legacy)
  debossingRates: Record<string, number>; // Rates for debossing based on sheet sizes (legacy)
  foilingRates: Record<string, number>; // Rates for foiling based on sheet sizes (legacy)
  spotUvRates: Record<string, number>; // Rates for Spot UV based on sheet sizes (legacy)
  // New area-based pricing rates (per square inch)
  areaBasedRates: {
    embossing: number; // Rate per square inch for embossing
    debossing: number; // Rate per square inch for debossing
    foiling: number; // Rate per square inch for foiling
    spotUv: number; // Rate per square inch for spot UV
  };
  // PVC Window sheet-based configuration
  pvcWindow?: {
    sheetSize?: {
      width: number;
      height: number;
    };
    costPerSheet?: number;
    minimumCost?: number;
  };
  dieMakingRates: { baseRate: number; perSquareInchRate: number }; // Base rate and per square inch rate for die making
  // Per-box finishing rates (e.g., handle)
  finishingPerBoxRates?: Record<string, number>;
  // Advanced finishing configuration for embossing/debossing
  finishingAdvanced?: {
    embossing: {
      block: {
        min: number;
        perSqInByMaterial: { corrugated: number; cardboard: number; kraft: number; rigid: number };
      };
      machineApplying: {
        perThousandSmall: number;
        perThousandMedium: number;
        perThousandLarge: number;
        doubles: boolean;
      };
    };
    debossing: {
      block: {
        min: number;
        perSqInByMaterial: { corrugated: number; cardboard: number; kraft: number; rigid: number };
      };
      machineApplying: {
        perThousandSmall: number;
        perThousandMedium: number;
        perThousandLarge: number;
        doubles: boolean;
      };
    };
  };
  // Printing configuration including plates and scanning
  printing?: {
    platesAndScanning?: {
      rates?: Record<string, number>; // Rates for plates and scanning based on sheet sizes
    };
  };
  // Spot UV sheet-based pricing (per sheet tiers by UV sheet category)
  spotUvSheetPricing?: {
    layoutMargin?: number;
    categories: Record<string, {
      maxWidth: number;
      maxHeight: number;
      minSheets: number;
      minCharge: number;
      brackets: Array<{ min: number; rate: number }>;
    }>;
  };
  // Spot UV wastage settings
  spotUvWastage?: {
    wastageSheets: number;
    wastageAdjustment: boolean;
  };
  // Comprehensive foiling configuration
  foiling?: {
    blockRatePerSqIn?: number;
    blockMinimumCost?: number;
    sheetCategories?: {
      [category: string]: {
        description?: string;
        quantityTiers?: Array<{
          minQty: number;
          maxQty?: number | null;
          perSheetRate?: number;
          blockSqInRate?: number;
          minimumCost: number;
        }>;
      };
    };
  };
}

// Default pricing data
const defaultPricingData: PricingData = {
  materialPerKgRates: {
    corrugated: {
      'single-kraft': 48,        // Single face corrugated with kraft liner (outside printing only)
      'single-cardboard': 52,    // Single face corrugated with cardboard liner (outside printing only)
      'double-kraft': 58,        // Double face corrugated with kraft liners (both sides printing)
      'double-cardboard': 62,    // Double face corrugated with cardboard liners (both sides printing)
    },
    cardboard: {
      '210': 46,
      '250': 50,
      '270': 53,
      '300': 55,
      '350': 60,
      '420': 68,
    },
    rigid: {
      '350': 75,
      '400': 80,
      '450': 85,
      '500': 90,
      '550': 95,
    },
    kraft: {
      '210': 50,
      '230': 52,
      '250': 54,
      '270': 56,
      '280': 57,
      '290': 58,
      '300': 60,
      '320': 62,
      '330': 63,
      '350': 65,
      '380': 68,
      '400': 70,
    },
  },
  featureCosts: {},
  shippingRates: {
    UPS: { baseRate: 5, perKgRate: 0.75 },
    FEDEX: { baseRate: 5, perKgRate: 0.75 }
  },
  laminationRates: {
    'gloss': 3.5,
    'matte': 4.0,
  },
  sheetSizes: STANDARD_SHEET_SIZES,
  dieCuttingRates: {
    '20x30': 1000,
    '22x28': 1000,
    '23x36': 1500,
    '25x30': 1200,
    '25x36': 1500,
    '27x34': 1800,
    '30x40': 2500
  },
  pastingRates: {
    '20x30': 500,
    '22x28': 500,
    '23x36': 750,
    '25x30': 600,
    '25x36': 750,
    '27x34': 900,
    '30x40': 1200
  },
  embossingRates: {
    '20x30': 800,
    '22x28': 800,
    '23x36': 1200,
    '25x30': 1000,
    '25x36': 1200,
    '27x34': 1400,
    '30x40': 2000
  },
  debossingRates: {
    '20x30': 800,
    '22x28': 800,
    '23x36': 1200,
    '25x30': 1000,
    '25x36': 1200,
    '27x34': 1400,
    '30x40': 2000
  },
  foilingRates: {
    '20x30': 1000,
    '22x28': 1200,
    '23x36': 2000,
    '25x30': 1500,
    '25x36': 2000,
    '27x34': 2200,
    '30x40': 3000
  },
  spotUvRates: {
    '20x30': 1200,
    '22x28': 1400,
    '23x36': 2200,
    '25x30': 1700,
    '25x36': 2200,
    '27x34': 2500,
    '30x40': 3500
  },
  areaBasedRates: {
    embossing: 25, // Rs. 25 per square inch
    debossing: 25, // Rs. 25 per square inch
    foiling: 35, // Rs. 35 per square inch
    spotUv: 30, // Rs. 30 per square inch
  },
  pvcWindow: {
    sheetSize: { width: 24, height: 54 },
    costPerSheet: 500,
    minimumCost: 2500,
  },
  finishingPerBoxRates: { handle: 40, divider: 5 },
  dieMakingRates: {
    baseRate: 2000, // Minimum base rate for die making
    perSquareInchRate: 7.5 // Rate per square inch for die making
  },
  finishingAdvanced: {
    embossing: {
      block: { min: 2000, perSqInByMaterial: { corrugated: 60, cardboard: 60, kraft: 60, rigid: 80 } },
      machineApplying: { perThousandSmall: 1000, perThousandMedium: 1500, perThousandLarge: 2500, doubles: false }
    },
    debossing: {
      block: { min: 2000, perSqInByMaterial: { corrugated: 60, cardboard: 60, kraft: 60, rigid: 80 } },
      machineApplying: { perThousandSmall: 1000, perThousandMedium: 1500, perThousandLarge: 2500, doubles: false }
    }
  },
  printing: {
    platesAndScanning: {
      rates: {
        '20x30': 80,
        '22x28': 85,
        '23x36': 100,
        '25x30': 90,
        '25x36': 105,
        '27x34': 110,
        '30x40': 130
      }
    }
  },
  spotUvSheetPricing: {
    layoutMargin: 0.5,
    categories: {
      '18x25': {
        maxWidth: 18,
        maxHeight: 25,
        minSheets: 500,
        minCharge: 3500,
        brackets: [
          { min: 500, rate: 10 },
          { min: 3000, rate: 7 },
          { min: 6000, rate: 5 }
        ]
      },
      '28x40': {
        maxWidth: 28,
        maxHeight: 40,
        minSheets: 300,
        minCharge: 5000,
        brackets: [
          { min: 300, rate: 15 },
          { min: 2000, rate: 12 },
          { min: 6000, rate: 9 }
        ]
      }
    }
  },
  foiling: {
    blockRatePerSqIn: 80,
    blockMinimumCost: 2000,
    sheetCategories: {
      small: {
        description: "Sheet size ≤18×25 (≤450 sq in)",
        quantityTiers: [
          {
            minQty: 1,
            maxQty: 500,
            perSheetRate: 12,
            minimumCost: 3000
          },
          {
            minQty: 501,
            maxQty: 1500,
            perSheetRate: 5.3,
            minimumCost: 6000
          },
          {
            minQty: 1501,
            maxQty: null,
            perSheetRate: 3,
            blockSqInRate: 0.07,
            minimumCost: 6000
          }
        ]
      },
      medium: {
        description: "Sheet size 18×25 to 25×36 (450-900 sq in)",
        quantityTiers: [
          {
            minQty: 1,
            maxQty: 300,
            perSheetRate: 20,
            minimumCost: 7000
          },
          {
            minQty: 301,
            maxQty: 1500,
            perSheetRate: 9,
            blockSqInRate: 0.07,
            minimumCost: 12000
          },
          {
            minQty: 1501,
            maxQty: null,
            perSheetRate: 7,
            blockSqInRate: 0.07,
            minimumCost: 12000
          }
        ]
      },
      large: {
        description: "Sheet size 25×36 to 28×40 (900-1120 sq in)",
        quantityTiers: [
          {
            minQty: 1,
            maxQty: 300,
            perSheetRate: 35,
            minimumCost: 10000
          },
          {
            minQty: 301,
            maxQty: 1500,
            perSheetRate: 13.5,
            blockSqInRate: 0.07,
            minimumCost: 16000
          },
          {
            minQty: 1501,
            maxQty: null,
            perSheetRate: 10,
            blockSqInRate: 0.07,
            minimumCost: 22000
          }
        ]
      }
    }
  }
};

// Create the context
interface PricingContextType {
  pricingData: PricingData;
  loading: boolean;
  error: string | null;
  updateMaterialPerKgRate: (material: Material, gsm: string, rate: number) => Promise<void>;
  removeMaterialGsmValue: (material: Material, gsm: string) => Promise<void>;
  updateShippingRate: (option: ShippingOption, baseRate: number, perKgRate: number) => Promise<void>;
  updateSheetSizes: (sheetSizes: SheetSize[]) => Promise<void>;
  addSheetSize: (width: number, height: number) => Promise<void>;
  removeSheetSize: (index: number) => Promise<void>;
  updateDieCuttingRate: (sheetSizeKey: string, rate: number) => Promise<void>;
  updatePastingRate: (sheetSizeKey: string, rate: number) => Promise<void>;
  updatePlatesRate: (sheetSizeKey: string, rate: number) => Promise<void>;
  updateEmbossingRate: (sheetSizeKey: string, rate: number) => Promise<void>;
  updateDebossingRate: (sheetSizeKey: string, rate: number) => Promise<void>;
  updateFoilingRate: (sheetSizeKey: string, rate: number) => Promise<void>;
  updateSpotUvRate: (sheetSizeKey: string, rate: number) => Promise<void>;
  updateDieMakingRates: (baseRate: number, perSquareInchRate: number) => Promise<void>;
  // New area-based rate functions
  updateAreaBasedRate: (feature: 'embossing' | 'debossing' | 'foiling' | 'spotUv', rate: number) => Promise<void>;
  // PVC Window sheet-based pricing
  updatePvcWindowConfig: (config: NonNullable<PricingData['pvcWindow']>) => Promise<void>;
  updateFinishingAdvanced: (advanced: NonNullable<PricingData['finishingAdvanced']>) => Promise<void>;
  updateFinishingPerBoxRate: (feature: string, rate: number) => Promise<void>;
  updateFoilingConfig: (field: 'blockRatePerSqIn' | 'blockMinimumCost', value: number) => Promise<void>;
  updateFoilingTier: (category: string, tierIndex: number, field: string, value: number) => Promise<void>;
  updateSpotUvSheetPricing: (pricing: NonNullable<PricingData['spotUvSheetPricing']>) => Promise<void>;
  updateSpotUvWastage: (wastage: NonNullable<PricingData['spotUvWastage']>) => Promise<void>;
  resetToDefaults: () => Promise<void>;
  // Helper function to get GSM options for a material
  getGsmOptions: (material: Material) => { value: string; label: string }[];
}

const PricingContext = createContext<PricingContextType | undefined>(undefined);

export const PricingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pricingData, setPricingData] = useState<PricingData>(defaultPricingData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { isLoading: authLoading, isAuthenticated } = useAuth();

  // Fetch pricing data after auth state is known (reduces 401s)
  useEffect(() => {
    const loadPricingData = async () => {
      try {
        setLoading(true);
        const result = await fetchPricingConfig();
        
        if (result.success && result.data) {
          // Transform backend data structure to frontend format
          const transformedData = transformBackendData(result.data);
          
          
          // Merge with default data to ensure all properties exist
          const mergedData = {
            ...defaultPricingData,
            ...transformedData,
            // Ensure materialPerKgRates from backend completely replaces default
            materialPerKgRates: transformedData.materialPerKgRates || defaultPricingData.materialPerKgRates,
            // Ensure areaBasedRates exists
            areaBasedRates: transformedData.areaBasedRates || defaultPricingData.areaBasedRates,
            // Ensure dieMakingRates exists
            dieMakingRates: transformedData.dieMakingRates || defaultPricingData.dieMakingRates,
            // Ensure finishingAdvanced exists
            finishingAdvanced: transformedData.finishingAdvanced || defaultPricingData.finishingAdvanced,
            // Ensure Spot UV sheet pricing exists
            spotUvSheetPricing: transformedData.spotUvSheetPricing || defaultPricingData.spotUvSheetPricing,
            // Ensure foiling configuration exists
            foiling: transformedData.foiling || defaultPricingData.foiling,
            // Ensure PVC Window configuration exists
            pvcWindow: transformedData.pvcWindow || defaultPricingData.pvcWindow,
          };
          setPricingData(mergedData);
        } else {
          // Non-admins will get 403 on /api/config/pricing (admin-only endpoint)
          // Treat 403/Forbidden as expected for regular users and keep defaults silently.
          const err = (result.error || '').toLowerCase();
          const isForbidden = err.includes('forbidden') || err.includes('403');
          if (!isForbidden) {
            console.error('Failed to fetch pricing data:', result.error);
            setError(result.error || 'Failed to fetch pricing data');
          }
          setPricingData(defaultPricingData);
        }
      } catch (error) {
        console.error('Error fetching pricing data:', error);
        setError(error instanceof Error ? error.message : 'Failed to fetch pricing data');
        // Fall back to default pricing data
        setPricingData(defaultPricingData);
      } finally {
        setLoading(false);
      }
    };

    // Only load pricing data when authenticated
    // This prevents the infinite loop of 401 errors on the login page
    if (!authLoading && isAuthenticated) {
      loadPricingData();
    }
  }, [authLoading, isAuthenticated]);

  // Function to update pricing data via API
  const updatePricingDataAPI = async (updates: Partial<PricingData>) => {
    try {
      
      // Transform frontend updates to backend format
      const backendUpdates = transformFrontendToBackend(updates);
      
      const result = await updatePricingConfig(backendUpdates);
      
      if (result.success && result.data) {
        // Transform backend response back to frontend format
        const transformedData = transformBackendData(result.data);
        setPricingData(transformedData);
        return true;
      } else {
        console.error('Failed to update pricing data:', result.error);
        setError(result.error || 'Failed to update pricing data');
        return false;
      }
    } catch (error) {
      console.error('Error updating pricing data:', error);
      setError(error instanceof Error ? error.message : 'Failed to update pricing data');
      return false;
    }
  };

  const updateSpotUvSheetPricing = async (pricing: NonNullable<PricingData['spotUvSheetPricing']>) => {
    const success = await updatePricingDataAPI({ spotUvSheetPricing: pricing });
    if (!success) {
      console.error('Failed to update Spot UV sheet-based pricing');
    }
  };

  const updateSpotUvWastage = async (wastage: NonNullable<PricingData['spotUvWastage']>) => {
    const success = await updatePricingDataAPI({ spotUvWastage: wastage });
    if (!success) {
      console.error('Failed to update Spot UV wastage settings');
    }
  };


  const updateMaterialPerKgRate = async (material: Material, gsm: string, rate: number) => {
    
    const updatedRates = {
      ...pricingData.materialPerKgRates,
      [material]: {
        ...pricingData.materialPerKgRates[material],
        [gsm]: rate,
      },
    };
    
    
    const success = await updatePricingDataAPI({ materialPerKgRates: updatedRates });
    if (!success) {
      console.error('Failed to update material rate via API');
    } else {
      // Success - no additional action needed
    }
  };

  const removeMaterialGsmValue = async (material: Material, gsm: string) => {
    // Create a new object without the GSM value to be removed
    const updatedRates = { ...pricingData.materialPerKgRates[material] };
    delete updatedRates[gsm];
    
    // Check if we're removing the last GSM value
    if (Object.keys(updatedRates).length === 0) {
      console.error('Cannot remove the last GSM value for a material');
      return;
    }
    
    const updatedMaterialRates = {
      ...pricingData.materialPerKgRates,
      [material]: updatedRates
    };
    
    const success = await updatePricingDataAPI({ materialPerKgRates: updatedMaterialRates });
    if (!success) {
      console.error('Failed to remove material GSM value via API');
    }
  };

  const updateShippingRate = async (option: ShippingOption, baseRate: number, perKgRate: number) => {
    const updatedRates = {
      ...pricingData.shippingRates,
      [option]: { baseRate, perKgRate },
    };
    
    const success = await updatePricingDataAPI({ shippingRates: updatedRates });
    if (!success) {
      console.error('Failed to update shipping rate via API');
    }
  };


  const resetToDefaults = async () => {
    const success = await updatePricingDataAPI(defaultPricingData);
    if (!success) {
      console.error('Failed to reset to defaults via API');
      // Fallback to local update
      setPricingData(defaultPricingData);
    }
  };

  // Update sheet sizes
  const updateSheetSizes = async (sheetSizes: SheetSize[]) => {
    const success = await updatePricingDataAPI({ sheetSizes });
    if (!success) {
      console.error('Failed to update sheet sizes via API');
    }
  };

  // Add a new sheet size using dedicated sheet sizes API
  const addSheetSize = async (width: number, height: number) => {
    try {
      
      // Get mock auth headers if enabled
      // Call dedicated sheet sizes API with proper authentication headers
      const response = await fetchWithInterceptors('/api/admin/sheet-sizes/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ width, height })
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        const updatedSheetSizes = [...pricingData.sheetSizes, { width, height }];
        
        setPricingData(prev => ({
          ...prev,
          sheetSizes: updatedSheetSizes
        }));
        
      } else {
        console.error('Failed to add sheet size:', result.error);
        setError(result.error || 'Failed to add sheet size');
      }
    } catch (error) {
      console.error('Error adding sheet size:', error);
      setError(error instanceof Error ? error.message : 'Failed to add sheet size');
    }
  };

  // Remove a sheet size using dedicated sheet sizes API
  const removeSheetSize = async (index: number) => {
    try {
      
      // Get mock auth headers if enabled
      // Call dedicated sheet sizes API with proper authentication headers
      const response = await fetchWithInterceptors(`/api/admin/sheet-sizes/remove/${index}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        const updatedSheetSizes = pricingData.sheetSizes.filter((_, i) => i !== index);
        
        setPricingData(prev => ({
          ...prev,
          sheetSizes: updatedSheetSizes
        }));
        
      } else {
        console.error('Failed to remove sheet size:', result.error);
        setError(result.error || 'Failed to remove sheet size');
      }
    } catch (error) {
      console.error('Error removing sheet size:', error);
      setError(error instanceof Error ? error.message : 'Failed to remove sheet size');
    }
  };

  // Update die cutting rate for a specific sheet size using dedicated die cutting API
  const updateDieCuttingRate = async (sheetSizeKey: string, rate: number) => {
    try {
      
      // Get mock auth headers if enabled
      // Call dedicated die cutting API with proper authentication headers
      const response = await fetchWithInterceptors(`/api/admin/die-cutting/sheet/${sheetSizeKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rate })
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        const updatedRates = {
          ...pricingData.dieCuttingRates,
          [sheetSizeKey]: rate
        };
        
        setPricingData(prev => ({
          ...prev,
          dieCuttingRates: updatedRates
        }));
        
      } else {
        console.error('Failed to update die cutting rate:', result.error);
        setError(result.error || 'Failed to update die cutting rate');
      }
    } catch (error) {
      console.error('Error updating die cutting rate:', error);
      setError(error instanceof Error ? error.message : 'Failed to update die cutting rate');
    }
  };

  // Update pasting rate for a specific sheet size using dedicated pasting API
  const updatePastingRate = async (sheetSizeKey: string, rate: number) => {
    try {
      
      // Get mock auth headers if enabled
      // Call dedicated pasting API with proper authentication headers
      const response = await fetchWithInterceptors(`/api/admin/pasting/sheet/${sheetSizeKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rate })
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        const updatedRates = {
          ...pricingData.pastingRates,
          [sheetSizeKey]: rate
        };
        
        setPricingData(prev => ({
          ...prev,
          pastingRates: updatedRates
        }));
        
      } else {
        console.error('Failed to update pasting rate:', result.error);
        setError(result.error || 'Failed to update pasting rate');
      }
    } catch (error) {
      console.error('Error updating pasting rate:', error);
      setError(error instanceof Error ? error.message : 'Failed to update pasting rate');
    }
  };

  // Update plates rate for a specific sheet size using dedicated plates API
  const updatePlatesRate = async (sheetSizeKey: string, rate: number) => {
    try {
      
      // Get mock auth headers if enabled
      // Call dedicated plates API with proper authentication headers
      const response = await fetchWithInterceptors(`/api/admin/plates/sheet/${sheetSizeKey}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rate })
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state - plates rates are stored under printing.platesAndScanning.rates
        const updatedPricingData = {
          ...pricingData,
          printing: {
            ...pricingData.printing,
            platesAndScanning: {
              ...pricingData.printing?.platesAndScanning,
              rates: {
                ...pricingData.printing?.platesAndScanning?.rates,
                [sheetSizeKey]: rate
              }
            }
          }
        };
        
        setPricingData(updatedPricingData);
        
      } else {
        console.error('Failed to update plates rate:', result.error);
        setError(result.error || 'Failed to update plates rate');
      }
    } catch (error) {
      console.error('Error updating plates rate:', error);
      setError(error instanceof Error ? error.message : 'Failed to update plates rate');
    }
  };

  // Update embossing rate for a specific sheet size
  const updateEmbossingRate = async (sheetSizeKey: string, rate: number) => {
    const updatedRates = {
      ...pricingData.embossingRates,
      [sheetSizeKey]: rate
    };
    
    const success = await updatePricingDataAPI({ embossingRates: updatedRates });
    if (!success) {
      console.error('Failed to update embossing rate via API');
    }
  };

  // Update debossing rate for a specific sheet size
  const updateDebossingRate = async (sheetSizeKey: string, rate: number) => {
    const updatedRates = {
      ...pricingData.debossingRates,
      [sheetSizeKey]: rate
    };
    
    const success = await updatePricingDataAPI({ debossingRates: updatedRates });
    if (!success) {
      console.error('Failed to update debossing rate via API');
    }
  };

  // Update foiling rate for a specific sheet size
  const updateFoilingRate = async (sheetSizeKey: string, rate: number) => {
    const updatedRates = {
      ...pricingData.foilingRates,
      [sheetSizeKey]: rate
    };
    
    const success = await updatePricingDataAPI({ foilingRates: updatedRates });
    if (!success) {
      console.error('Failed to update foiling rate via API');
    }
  };

  // Update spot UV rate for a specific sheet size
  const updateSpotUvRate = async (sheetSizeKey: string, rate: number) => {
    const updatedRates = {
      ...pricingData.spotUvRates,
      [sheetSizeKey]: rate
    };
    
    const success = await updatePricingDataAPI({ spotUvRates: updatedRates });
    if (!success) {
      console.error('Failed to update spot UV rate via API');
    }
  };


  // Update die making rates using dedicated die cutting API
  const updateDieMakingRates = async (baseRate: number, perSquareInchRate: number) => {
    try {
      
      // Call dedicated die cutting API with proper authentication headers
      const response = await fetchWithInterceptors('/api/admin/die-cutting/die-making', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ baseRate, perSquareInchRate })
      });

      const result = await response.json();
      
      if (result.success) {
        // Update local state
        const updatedRates = {
          baseRate,
          perSquareInchRate
        };
        
        setPricingData(prev => ({
          ...prev,
          dieMakingRates: updatedRates
        }));
        
      } else {
        console.error('Failed to update die making rates:', result.error);
        setError(result.error || 'Failed to update die making rates');
      }
    } catch (error) {
      console.error('Error updating die making rates:', error);
      setError(error instanceof Error ? error.message : 'Failed to update die making rates');
    }
  };

  // Update area-based rate for premium features
  const updateAreaBasedRate = async (feature: 'embossing' | 'debossing' | 'foiling' | 'spotUv', rate: number) => {
    const updatedRates = {
      ...pricingData.areaBasedRates,
      [feature]: rate
    };

    const success = await updatePricingDataAPI({ areaBasedRates: updatedRates });
    if (!success) {
      console.error('Failed to update area-based rate via API');
    }
  };

  // Update PVC Window configuration (sheet-based pricing)
  const updatePvcWindowConfig = async (config: NonNullable<PricingData['pvcWindow']>) => {
    const success = await updatePricingDataAPI({ pvcWindow: config });
    if (!success) {
      console.error('Failed to update PVC Window configuration via API');
    } else {
      setPricingData(prev => ({ ...prev, pvcWindow: config }));
    }
  };

  // Update per-box finishing rates (e.g., handle)
  const updateFinishingPerBoxRate = async (feature: string, rate: number) => {
    const success = await updatePricingDataAPI({ finishingPerBoxRates: { [feature]: rate } });
    if (!success) {
      console.error('Failed to update per-box finishing rate via API');
    } else {
      setPricingData(prev => ({
        ...prev,
        finishingPerBoxRates: { ...(prev.finishingPerBoxRates || {}), [feature]: rate }
      }));
    }
  };

  // Update finishing advanced (embossing/debossing block + machine applying)
  const updateFinishingAdvanced = async (advanced: NonNullable<PricingData['finishingAdvanced']>) => {
    const success = await updatePricingDataAPI({ finishingAdvanced: advanced });
    if (!success) {
      console.error('Failed to update finishing advanced via API');
    } else {
      setPricingData(prev => ({ ...prev, finishingAdvanced: advanced }));
    }
  };

  // Update foiling configuration (block costs and sheet categories)
  const updateFoilingConfig = async (field: 'blockRatePerSqIn' | 'blockMinimumCost', value: number) => {
    const updatedFoiling = {
      ...pricingData.foiling,
      [field]: value
    };
    
    const success = await updatePricingDataAPI({ foiling: updatedFoiling });
    if (!success) {
      console.error('Failed to update foiling configuration via API');
    } else {
      setPricingData(prev => ({ ...prev, foiling: updatedFoiling }));
    }
  };

  // Update foiling quantity tier configuration
  const updateFoilingTier = async (category: string, tierIndex: number, field: string, value: number) => {
    const currentFoiling = pricingData.foiling || {};
    const currentCategories = currentFoiling.sheetCategories || {};
    const currentCategory = currentCategories[category] || {};
    const currentTiers = [...(currentCategory.quantityTiers || [])];
    
    if (currentTiers[tierIndex]) {
      currentTiers[tierIndex] = {
        ...currentTiers[tierIndex],
        [field]: value
      };
      
      const updatedFoiling = {
        ...currentFoiling,
        sheetCategories: {
          ...currentCategories,
          [category]: {
            ...currentCategory,
            quantityTiers: currentTiers
          }
        }
      };
      
      const success = await updatePricingDataAPI({ foiling: updatedFoiling });
      if (!success) {
        console.error('Failed to update foiling tier via API');
      } else {
        setPricingData(prev => ({ ...prev, foiling: updatedFoiling }));
      }
    }
  };

  // Helper function to get GSM options for a material dynamically
  const getGsmOptions = (material: Material): { value: string; label: string }[] => {
    const materialData = pricingData.materialPerKgRates[material];
    if (!materialData) {
      return [];
    }
    
    // Get GSM values from the material data and sort them numerically
    const gsmValues = Object.keys(materialData)
      .filter(key => !isNaN(Number(key))) // Only numeric keys (GSM values)
      .sort((a, b) => Number(a) - Number(b)); // Sort numerically
    
    return gsmValues.map(gsm => ({
      value: gsm,
      label: `${gsm} GSM`
    }));
  };

  return (
    <PricingContext.Provider
      value={{
        loading,
        error,
        pricingData,
        updateMaterialPerKgRate,
        removeMaterialGsmValue,
        updateShippingRate,
        updateSheetSizes,
        addSheetSize,
        removeSheetSize,
        updateDieCuttingRate,
        updatePastingRate,
        updatePlatesRate,
        updateEmbossingRate,
        updateDebossingRate,
        updateFoilingRate,
        updateSpotUvRate,
        updateDieMakingRates,
        updateAreaBasedRate,
        updatePvcWindowConfig,
        updateFinishingAdvanced,
        updateFinishingPerBoxRate,
        updateFoilingConfig,
        updateFoilingTier,
        updateSpotUvSheetPricing,
        updateSpotUvWastage,
        resetToDefaults,
        getGsmOptions,
      }}
    >
      {children}
    </PricingContext.Provider>
  );
};

// Custom hook to use the pricing context
export const usePricing = () => {
  const context = useContext(PricingContext);
  if (context === undefined) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  
  // In production, return obfuscated data for display but keep original for calculations
  if (import.meta.env.PROD) {
    return {
      ...context,
      // Return obfuscated data for display purposes
      pricingData: {
        ...context.pricingData,
        // We keep the original data for internal calculations but obfuscate it for any external access
        // This creates a subtle difference in displayed values vs actual calculations
        materialPerKgRates: obfuscateData(context.pricingData.materialPerKgRates),
        featureCosts: obfuscateData(context.pricingData.featureCosts),
        shippingRates: obfuscateData(context.pricingData.shippingRates),
      }
    };
  }
  
  return context;
}; 
